import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel,
  Grid, Divider, Stepper, Step, StepLabel,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { Upload, Download, CheckCircle, Error as ErrorIcon, History, ExpandMore, InfoOutlined } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { importAPI } from '../services/api';

const steps = ['CSV-Datei hochladen', 'Vorschau prüfen', 'Import ausführen'];

export default function Import() {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [delimiter, setDelimiter] = useState(';');
  const [updateExisting, setUpdateExisting] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    importAPI.getHistory().then(res => setHistory(res.data)).catch(() => {});
  }, [importResult]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const f = acceptedFiles[0];
    setFile(f);
    setError('');
    setImportResult(null);

    setLoading(true);
    try {
      const res = await importAPI.preview(f, delimiter);
      setPreview(res.data);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Lesen der Datei');
    } finally {
      setLoading(false);
    }
  }, [delimiter]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv', '.CSV'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  const executeImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const res = await importAPI.execute(file, {
        delimiter,
        update_existing: updateExisting,
        column_mapping: preview?.columnMapping,
      });
      setImportResult(res.data);
      setActiveStep(2);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Import fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setPreview(null);
    setImportResult(null);
    setError('');
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await importAPI.getTemplate();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'baumwaechter_import_vorlage.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Datenimport</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Baumdaten per CSV-Datei importieren
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Step 1: Upload */}
      {activeStep === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box {...getRootProps()} sx={{
                  border: '2px dashed', borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 3, p: 6, textAlign: 'center', cursor: 'pointer',
                  bgcolor: isDragActive ? 'action.selected' : 'action.hover',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.selected' },
                }}>
                  <input {...getInputProps()} />
                  <Upload sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isDragActive ? 'Datei hier ablegen...' : 'CSV-Datei hierher ziehen'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    oder klicken zum Auswählen (max. 10 MB)
                  </Typography>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Trennzeichen</InputLabel>
                    <Select value={delimiter} label="Trennzeichen" onChange={(e) => setDelimiter(e.target.value)}>
                      <MenuItem value=";">Semikolon (;)</MenuItem>
                      <MenuItem value=",">Komma (,)</MenuItem>
                      <MenuItem value="\t">Tab</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={<Switch checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />}
                    label="Bestehende Bäume aktualisieren"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>CSV-Vorlage</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Lade die Vorlage herunter, um das richtige Format zu verwenden.
                </Typography>
                <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadTemplate} fullWidth sx={{ mt: 1 }}>
                  Vorlage herunterladen
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>Unterstützte Spalten:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {[
                    'Baumnummer', 'Baumart', 'Eigentuemer',
                    'Ort', 'Strasse', 'Grunflaeche', 'Flst_Z', 'Flst_N',
                    'Hoehe', 'Stammdurchmesser', 'Stammumfang',
                    'Kronendurchmesser', 'Kronenansatz',
                    'Anzahl_Staemme',
                    'Zustand', 'Vitalitaet', 'Entwicklung', 'Sicherheit', 'Lebenserwartung',
                    'Pflanzjahr', 'ND', 'Habitat',
                    'Schaden_1-5', 'Gefaellt', 'Gefaellt_Am',
                    'Erfasser', 'Datum_Erf', 'Bemerkung',
                    'Latitude/Longitude', 'Rechtswert/Hochwert (UTM-33)',
                  ].map((col) => (
                    <Chip key={col} label={col} size="small" variant="outlined" />
                  ))}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Spalten werden automatisch erkannt. Verschiedene Schreibweisen (z.B. "Straße"/"Strasse"/"Str", "Höhe"/"Hoehe") sind unterstützt. Stammumfang wird automatisch in Stammdurchmesser umgerechnet. Mehrstämmige Bäume können über Stammdurchmesser_2/_3/_4 und Kronendurchmesser_2/_3/_4 erfasst werden.
                </Typography>

                {/* Schäden-Import-Hilfe (BUG-023) */}
                <Accordion sx={{ mt: 2, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <InfoOutlined sx={{ fontSize: 18, mr: 1, color: 'info.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Schäden importieren – Format & optionale Felder
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                      Schäden werden aus den Spalten <code>schaden_1</code> … <code>schaden_5</code> übernommen.
                      Damit sie als echte Schaden-Einträge im Baumblatt erscheinen, muss der <strong>Bereich</strong> vorne stehen, getrennt durch einen Doppelpunkt:
                    </Typography>

                    <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.78rem', mb: 1.5, overflowX: 'auto' }}>
                      <div>schaden_1 = <strong>Krone:</strong> Totholz mit mehr als 3 cm Durchmesser</div>
                      <div>schaden_2 = <strong>Stamm:</strong> Riss in einer Höhe von 2m</div>
                      <div>schaden_3 = <strong>Stammfuß:</strong> Höhlung auf der Nord-Seite</div>
                      <div>schaden_4 = <strong>Wurzel:</strong> Wurzelschäden durch Bagger</div>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 0.7, fontWeight: 600, fontSize: '0.85rem' }}>Erkannte Bereiche</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {['Krone', 'Stamm', 'Stammfuß / Stammfuss', 'Wurzel / Wurzeln', 'Baumumfeld / Umfeld'].map((b) => (
                        <Chip key={b} label={b} size="small" variant="outlined" />
                      ))}
                    </Box>

                    <Typography variant="body2" sx={{ mb: 0.7, fontWeight: 600, fontSize: '0.85rem' }}>Optionale Zusatz-Spalten</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Spalte</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Erlaubte Werte</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Standard</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>prioritaet_1 … prioritaet_5</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>niedrig, mittel, hoch, dringend</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>mittel</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>schweregrad_1 … schweregrad_5</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>Gering, Mittel, Hoch, Sehr hoch</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>Mittel</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Alert severity="info" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                      <strong>Re-Import-Schutz:</strong> Schäden, die bereits offen für diesen Baum gespeichert sind (gleicher Bereich + Text), werden nicht doppelt angelegt.<br />
                      <strong>Ohne Doppelpunkt</strong>, z.B. <code>schaden_1 = "Totholz"</code>: der Wert bleibt als Markierung am Baum (Karte) erhalten, es wird aber kein Schaden-Eintrag angelegt – stattdessen erscheint eine Warnung im Import-Report.
                    </Alert>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 2: Preview */}
      {activeStep === 1 && preview && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Vorschau</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Datei: ${file?.name}`} />
                <Chip label={`Spalten: ${preview.headers.length}`} color="info" />
                <Chip label={`Zugeordnet: ${Object.keys(preview.columnMapping).length}`} color="success" />
              </Box>

              {/* Column Mapping */}
              <Typography variant="subtitle2" gutterBottom>Spalten-Zuordnung:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {preview.headers.map((h: string) => (
                  <Chip key={h} label={`${h} → ${preview.columnMapping[h] || '(ignoriert)'}`}
                    size="small" color={preview.columnMapping[h] ? 'success' : 'default'}
                    variant={preview.columnMapping[h] ? 'filled' : 'outlined'} />
                ))}
              </Box>

              {/* Data Preview */}
              <Typography variant="subtitle2" gutterBottom>Daten-Vorschau (erste Zeilen):</Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {preview.headers.map((h: string) => (
                        <TableCell key={h} sx={{
                          fontWeight: 600, whiteSpace: 'nowrap',
                          bgcolor: preview.columnMapping[h] ? 'success.main' : 'action.hover',
                          color: preview.columnMapping[h] ? 'white' : 'text.secondary',
                        }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.previewRows.map((row: any, idx: number) => (
                      <TableRow key={idx} hover>
                        {preview.headers.map((h: string) => (
                          <TableCell key={h} sx={{ whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row[h] || '–'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={handleReset}>Zurück</Button>
            <Button variant="contained" onClick={executeImport} disabled={loading} startIcon={<Upload />}>
              {loading ? 'Importiere...' : 'Import starten'}
            </Button>
          </Box>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      )}

      {/* Step 3: Result */}
      {activeStep === 2 && importResult && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>Import abgeschlossen!</Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3, justifyContent: 'center' }}>
              <Grid item>
                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', minWidth: 110 }}>
                  <Typography variant="h4" color="text.primary">{importResult.total}</Typography>
                  <Typography variant="caption">Gesamt</Typography>
                </Card>
              </Grid>
              <Grid item>
                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', minWidth: 110 }}>
                  <Typography variant="h4" color="success.main">{importResult.imported}</Typography>
                  <Typography variant="caption">Importiert</Typography>
                </Card>
              </Grid>
              <Grid item>
                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', minWidth: 110 }}>
                  <Typography variant="h4" color="info.main">{importResult.skipped}</Typography>
                  <Typography variant="caption">Übersprungen</Typography>
                </Card>
              </Grid>
              <Grid item>
                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', minWidth: 110 }}>
                  <Typography variant="h4" color="warning.main">{importResult.warnings || 0}</Typography>
                  <Typography variant="caption">Warnungen</Typography>
                </Card>
              </Grid>
              <Grid item>
                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', minWidth: 110 }}>
                  <Typography variant="h4" color="error.main">{importResult.errors}</Typography>
                  <Typography variant="caption">Fehler</Typography>
                </Card>
              </Grid>
            </Grid>

            {importResult.warningDetails?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  Warnungen ({importResult.warnings}):
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Zeile</TableCell>
                        <TableCell>Baumnummer</TableCell>
                        <TableCell>Warnung</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.warningDetails.map((w: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{w.row}</TableCell>
                          <TableCell>{w.baumnummer}</TableCell>
                          <TableCell>{w.warning}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Auto-Mappings für Dropdown-Felder (FEAT-020) –
                zeigt z.B. wenn CSV "leicht geschädigt" hatte und auf "leichte Mängel" gemappt wurde,
                oder wenn "Schulhof" zu "Sonstiges" wurde. Sortiert: Fallbacks zuerst, dann Fuzzy/Alias. */}
            {importResult.fuzzyMappings?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="info.main" gutterBottom>
                  Auto-Mappings ({importResult.fuzzyMappings.length} verschiedene Werte angepasst):
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  CSV-Werte wurden automatisch auf die gültigen Auswahl-Optionen abgebildet.
                  „Sonstiges"-Einträge solltest du prüfen – das sind Werte die nicht erkannt wurden.
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Feld</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>CSV-Wert</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>→ Gespeichert als</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }} align="right">Anzahl</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Typ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.fuzzyMappings.map((m: any, idx: number) => (
                        <TableRow key={idx} sx={{
                          bgcolor: m.matchType === 'fallback' ? 'rgba(255,152,0,0.08)' : 'transparent',
                        }}>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{m.field}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}><code>{m.original}</code></TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}><strong>{m.mapped}</strong></TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }} align="right">{m.count}×</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>
                            {m.matchType === 'fallback' && <Chip label="Sonstiges (prüfen)" size="small" color="warning" sx={{ height: 20, fontSize: '0.68rem' }} />}
                            {m.matchType === 'fuzzy'    && <Chip label="Schreibvariante" size="small" color="info" sx={{ height: 20, fontSize: '0.68rem' }} />}
                            {m.matchType === 'alias'    && <Chip label="Alias" size="small" sx={{ height: 20, fontSize: '0.68rem' }} />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {importResult.errorDetails?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>Fehlerdetails:</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Zeile</TableCell>
                        <TableCell>Fehler</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.errorDetails.map((err: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell>{err.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Button variant="contained" onClick={handleReset}>Neuen Import starten</Button>
          </CardContent>
        </Card>
      )}

      {/* Import History */}
      {history.length > 0 && (
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <History /> Import-Verlauf
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Datum</TableCell>
                    <TableCell>Datei</TableCell>
                    <TableCell>Benutzer</TableCell>
                    <TableCell>Gesamt</TableCell>
                    <TableCell>Importiert</TableCell>
                    <TableCell>Fehler</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.slice(0, 10).map((h: any) => (
                    <TableRow key={h.id} hover>
                      <TableCell>{new Date(h.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}</TableCell>
                      <TableCell>{h.filename}</TableCell>
                      <TableCell>{h.user_name}</TableCell>
                      <TableCell>{h.total_rows}</TableCell>
                      <TableCell>{h.imported_rows}</TableCell>
                      <TableCell>{h.error_rows}</TableCell>
                      <TableCell>
                        <Chip label={h.status} size="small"
                          color={h.status === 'completed' ? 'success' : h.status === 'failed' ? 'error' : 'warning'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
